import { PageHeader } from "../components/PageHeader";
import { Reveal } from "../components/Reveal";
import { CtaBand } from "../components/CtaBand";
import { Marquee } from "../components/Marquee";
import { DEV_PILLARS, IMAGES } from "../data/site";

const APPROACH = [
  {
    num: "01",
    title: "Assessment first",
    text: "Every program starts with an honest baseline — data, video, fitness, and goals — so progress is measured, not assumed.",
  },
  {
    num: "02",
    title: "Integrated, not additive",
    text: "Physical training, simulator work, and mental preparation are woven into the racing calendar instead of bolted on around it.",
  },
  {
    num: "03",
    title: "Debrief everything",
    text: "Every session ends with structured analysis. Drivers leave each weekend knowing exactly what to work on next.",
  },
];

export default function DriverDevelopment() {
  return (
    <div data-testid="driver-development-page">
      <PageHeader
        overline="Driver Development"
        lines={["Faster is", "a skill.", "We train it."]}
        description="Coaching and performance programs for drivers who take their craft seriously — blending seat time, physical preparation, simulation, and the mental side of going quick."
        image={IMAGES.driverSuit}
        imageAlt="Driver in race suit preparing for a session"
      />

      <Marquee
        items={["Coaching", "Physical Training", "Simulator", "Mental Prep", "Strategy", "Analysis"]}
      />

      <section data-testid="dev-pillars" className="bg-white">
        <div className="mx-auto max-w-7xl px-5 py-20 md:px-8 md:py-28">
          <Reveal>
            <p className="font-overline text-[10px] uppercase tracking-[0.35em] text-[#e63946] md:text-[11px]">
              The development system
            </p>
            <h2 className="font-display mt-5 max-w-2xl text-3xl font-extrabold tracking-tight text-[#111111] sm:text-4xl lg:text-5xl">
              Six disciplines. One driver.
            </h2>
          </Reveal>
          <div className="mt-14 grid gap-px bg-neutral-200 sm:grid-cols-2 lg:grid-cols-3">
            {DEV_PILLARS.map((p, i) => (
              <div key={p.num} className="group bg-white p-8 transition-colors duration-300 hover:bg-[#111111] md:p-10">
                <Reveal delay={0.05 * i}>
                  <p className="font-overline text-[11px] tracking-[0.25em] text-neutral-400 transition-colors duration-300 group-hover:text-neutral-500">
                    {p.num}
                  </p>
                  <h3 className="font-display mt-4 text-xl font-bold tracking-tight text-[#111111] transition-colors duration-300 group-hover:text-white">
                    {p.title}
                  </h3>
                  <p className="mt-3 text-sm leading-relaxed text-neutral-600 transition-colors duration-300 group-hover:text-neutral-400">
                    {p.text}
                  </p>
                </Reveal>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="dev-approach" className="bg-[#f8f9fa]">
        <div className="mx-auto grid max-w-7xl gap-14 px-5 py-20 md:grid-cols-12 md:px-8 md:py-28">
          <div className="md:col-span-5">
            <Reveal delay={0.1}>
              <div className="clip-corner img-treatment overflow-hidden">
                <img
                  src={IMAGES.driverFocus}
                  alt="Helmeted driver focused between sessions"
                  className="aspect-[4/5] w-full object-cover"
                  loading="lazy"
                />
              </div>
              <div className="clip-corner img-treatment mt-5 overflow-hidden">
                <img
                  src={IMAGES.simRigs}
                  alt="D-BOX haptic simulator rigs used in ABS LLC driver development programs"
                  className="aspect-[4/3] w-full object-cover"
                  loading="lazy"
                />
              </div>
              <p className="font-overline mt-3 text-[9px] uppercase tracking-[0.3em] text-neutral-500">
                Simulator development — D-BOX haptic rigs
              </p>
            </Reveal>
          </div>
          <div className="md:col-span-7">
            <Reveal>
              <p className="font-overline text-[10px] uppercase tracking-[0.35em] text-[#e63946] md:text-[11px]">
                How we work
              </p>
              <h2 className="font-display mt-5 text-3xl font-extrabold tracking-tight text-[#111111] sm:text-4xl">
                Development that survives race-day pressure.
              </h2>
            </Reveal>
            <div className="mt-10">
              {APPROACH.map((a, i) => (
                <Reveal key={a.num} delay={0.06 * i}>
                  <div className="grid gap-3 border-t border-neutral-300 py-8 sm:grid-cols-12">
                    <p className="font-overline text-sm tracking-[0.2em] text-neutral-400 sm:col-span-2">{a.num}</p>
                    <h3 className="font-display text-lg font-bold tracking-tight text-[#111111] sm:col-span-4 md:text-xl">
                      {a.title}
                    </h3>
                    <p className="text-sm leading-relaxed text-neutral-600 sm:col-span-6">{a.text}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      <CtaBand title="Commit to getting quicker." accent="We'll build the plan." />
    </div>
  );
}
